import * as React from 'react'
import { BookOpen } from 'lucide-react'
import { motion } from 'motion/react'

import cn from '@config/tailwind/cn'

import type { CardAction } from './card-action'

export type LessonModuleCardSize = 'large' | 'small'

/** Shared dimensions for LessonModuleCard and cards that mirror its cover sizing (e.g. ContinueLessonCard). */
export const lessonModuleCardSizeStyles: Record<
  LessonModuleCardSize,
  { wrapper: string; img: { width: number; height: number } }
> = {
  large: { wrapper: 'srd:w-[272px] srd:h-[204px]', img: { width: 272, height: 204 } },
  small: { wrapper: 'srd:w-[235px] srd:h-[176px]', img: { width: 235, height: 176 } },
}

interface LessonModuleCardProps {
  id: string
  coverUrl: string | null
  action?: CardAction
  size?: LessonModuleCardSize
  className?: string
}

export function LessonModuleCard({ id, coverUrl, action, size = 'large', className }: LessonModuleCardProps) {
  const { wrapper, img } = lessonModuleCardSizeStyles[size]
  const interactiveProps = action ? { whileHover: { scale: 1.025 }, whileTap: { scale: 0.975 } } : {}

  const content = (
    <motion.div data-id={id} {...interactiveProps} className={cn(action && 'srd:cursor-pointer', wrapper, className)}>
      <div className='srd:rounded-2xl srd:overflow-hidden srd:w-full srd:h-full srd:bg-grey-10 srd:flex srd:items-center srd:justify-center'>
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=''
            width={img.width}
            height={img.height}
            draggable={false}
            loading='lazy'
            decoding='async'
            className='srd:w-full srd:h-full srd:object-cover'
          />
        ) : (
          <BookOpen className='srd:w-10 srd:h-10 srd:text-grey-40' />
        )}
      </div>
    </motion.div>
  )

  if (!action) return content
  if (action.type === 'link') return action.renderWrapper(content)
  return (
    <button type='button' onClick={action.onClick} className='srd:block'>
      {content}
    </button>
  )
}
